// @spec POL-STR-036
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  CategoryFormDialog,
  DeleteCategoryDialog,
  PolicyCategoryList,
  useCreatePolicyCategory,
  useDeletePolicyCategory,
  usePolicyCategories,
  useUpdatePolicyCategory,
} from '@/features/policies';
import type { PolicyCategory, ReviewFrequency } from '@/features/policies';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/policy-categories')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.policyCategories'),
  }),
  component: PolicyCategoriesPage,
});

/**
 * Policy categories settings page.
 *
 * Allows organization admins to:
 * - View all policy categories in a hierarchical list
 * - Create new categories (root or nested)
 * - Edit existing categories
 * - Delete empty categories
 */
function PolicyCategoriesPage() {
  const { t } = useTranslation('settings');
  const { isAdmin } = useOrgRole();

  // State for dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<PolicyCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<PolicyCategory | null>(null);
  const [parentForCreate, setParentForCreate] = useState<string | undefined>(undefined);

  // API hooks
  const { data: categoriesData, isLoading } = usePolicyCategories();
  const createMutation = useCreatePolicyCategory();
  const updateMutation = useUpdatePolicyCategory();
  const deleteMutation = useDeletePolicyCategory();

  const categories = categoriesData?.items ?? [];

  // Handlers
  const handleCreateOpen = () => {
    setParentForCreate(undefined);
    setIsCreateDialogOpen(true);
  };

  const handleAddSubcategory = (parent: PolicyCategory) => {
    setParentForCreate(parent.id);
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (category: PolicyCategory) => {
    setEditingCategory(category);
  };

  const handleDelete = (category: PolicyCategory) => {
    setDeletingCategory(category);
  };

  const handleCreateSubmit = async (data: {
    name: string;
    description?: string | null;
    parentCategoryId?: string | null;
    defaultReviewFrequency: ReviewFrequency;
    isActive: boolean;
  }) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success(t('settings:toast.categoryCreatedSuccessfully'));
      setIsCreateDialogOpen(false);
      setParentForCreate(undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create category';
      toast.error(message);
      throw error;
    }
  };

  const handleUpdateSubmit = async (data: {
    name: string;
    description?: string | null;
    parentCategoryId?: string | null;
    defaultReviewFrequency: ReviewFrequency;
    isActive: boolean;
  }) => {
    if (!editingCategory) return;
    try {
      await updateMutation.mutateAsync({
        id: editingCategory.id,
        data,
      });
      toast.success(t('settings:toast.categoryUpdatedSuccessfully'));
      setEditingCategory(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update category';
      toast.error(message);
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCategory) return;
    try {
      await deleteMutation.mutateAsync(deletingCategory.id);
      toast.success(t('settings:toast.categoryDeletedSuccessfully'));
      setDeletingCategory(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete category';
      toast.error(message);
      throw error;
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <h2 className="text-lg font-medium text-destructive">
              {t('policyCategoriesPage.accessDeniedTitle')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('policyCategoriesPage.accessDeniedDescription')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        <header className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">
              {t('policyCategoriesPage.title')}
            </h1>
            <p className="mt-1 text-muted-foreground">{t('policyCategoriesPage.description')}</p>
          </div>

          <Button onClick={handleCreateOpen}>
            <Plus className="mr-2 h-4 w-4" />
            {t('policyCategoriesPage.addCategory')}
          </Button>
        </header>

        <PolicyCategoryList
          categories={categories}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddSubcategory={handleAddSubcategory}
        />

        {/* Create Dialog */}
        <CategoryFormDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          parentCategoryId={parentForCreate}
          allCategories={categories}
          onSubmit={handleCreateSubmit}
          isSubmitting={createMutation.isPending}
        />

        {/* Edit Dialog */}
        {editingCategory && (
          <CategoryFormDialog
            open={!!editingCategory}
            onOpenChange={(open) => {
              if (!open) setEditingCategory(null);
            }}
            category={editingCategory}
            parentCategoryId={editingCategory.parentCategoryId ?? undefined}
            allCategories={categories}
            onSubmit={handleUpdateSubmit}
            isSubmitting={updateMutation.isPending}
          />
        )}

        {/* Delete Dialog */}
        {deletingCategory && (
          <DeleteCategoryDialog
            open={!!deletingCategory}
            onOpenChange={(open) => {
              if (!open) setDeletingCategory(null);
            }}
            category={deletingCategory}
            onConfirm={handleDeleteConfirm}
            isDeleting={deleteMutation.isPending}
          />
        )}
      </div>
    </div>
  );
}
